import { makeAutoObservable } from 'mobx';
import PromoCodeModel, {
  PromoCode,
  CreatePromoCodeData,
  UpdatePromoCodeData,
} from '../../../model/professional/promo-code-model';
import toastStore from '../../../store/toast-store';
import promoCodeStore from '../../../store/promo-code-store';
import { HTTP_STATUS_CODE } from '../../../utils/UHttp';

class PromoCodeViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private promoCodeModel: typeof PromoCodeModel;

  constructor() {
    makeAutoObservable(this);
    this.promoCodeModel = PromoCodeModel;
  }

  /**
   * Gère les états de chargement de manière centralisée
   */
  private setLoadingStates(isLoading: boolean, resetError: boolean = true): void {
    this.setLoading(isLoading);
    if (resetError && !isLoading) {
      this.setError(null);
    }
  }

  /**
   * Gère les erreurs de manière centralisée
   */
  private handleError(message: string, error?: any): void {
    console.error(message, error);
    this.setError(message);
    toastStore.error(message);
  }

  setLoading(value: boolean): void {
    this.isLoading = value;
    promoCodeStore.setIsLoading(value);
  }

  setError(value: string | null): void {
    this.error = value;
  }

  /**
   * Charge tous les codes promo d'une entreprise
   */
  async loadPromoCodesByCompany(companyId: string): Promise<void> {
    this.setLoadingStates(true);

    try {
      const response = await this.promoCodeModel.getPromoCodesByCompany(companyId);

      if (response.success && response.data) {
        promoCodeStore.setPromoCodes(response.data);
      } else {
        this.handleError('Erreur lors du chargement des codes promo');
      }
    } catch (error: any) {
      this.handleError('Erreur lors du chargement des codes promo', error);
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Charge les codes promo actifs d'une entreprise
   */
  async loadActivePromoCodesByCompany(companyId: string): Promise<void> {
    this.setLoadingStates(true);

    try {
      const response = await this.promoCodeModel.getActivePromoCodesByCompany(companyId);

      if (response.success && response.data) {
        promoCodeStore.setPromoCodes(response.data);
      } else {
        this.handleError('Erreur lors du chargement des codes promo actifs');
      }
    } catch (error: any) {
      this.handleError('Erreur lors du chargement des codes promo actifs', error);
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Vérifie si l'owner peut créer un code promo
   */
  async checkCanCreatePromoCode(companyId: string): Promise<void> {
    try {
      const response = await this.promoCodeModel.canCreatePromoCode(companyId);

      if (response.success && response.data) {
        promoCodeStore.setCanCreate(response.data.canCreate, response.data.message);
      }
    } catch (error: any) {
      console.error('Erreur lors de la vérification des permissions', error);
    }
  }

  /**
   * Crée un nouveau code promo
   */
  async createPromoCode(companyId: string, data: CreatePromoCodeData): Promise<boolean> {
    this.setLoadingStates(true);

    try {
      const response = await this.promoCodeModel.createPromoCode(companyId, data);

      if (response.success && response.data && response.status === HTTP_STATUS_CODE.CREATED) {
        promoCodeStore.addPromoCode(response.data);
        toastStore.success(response.message || 'Code promo créé avec succès');
        return true;
      } else {
        this.handleError(response.message || 'Erreur lors de la création du code promo');
        return false;
      }
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la création du code promo', error);
      return false;
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Met à jour un code promo
   */
  async updatePromoCode(id: string, data: UpdatePromoCodeData): Promise<boolean> {
    this.setLoadingStates(true);

    try {
      const response = await this.promoCodeModel.updatePromoCode(id, data);

      if (response.success && response.data && response.status === HTTP_STATUS_CODE.OK) {
        promoCodeStore.updatePromoCode(id, response.data);
        toastStore.success(response.message || 'Code promo mis à jour avec succès');
        return true;
      } else {
        this.handleError(response.message || 'Erreur lors de la mise à jour du code promo');
        return false;
      }
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la mise à jour du code promo', error);
      return false;
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Active ou désactive un code promo
   */
  async togglePromoCode(id: string, isActive: boolean): Promise<boolean> {
    try {
      const response = await this.promoCodeModel.togglePromoCode(id, isActive);

      if (response.success && response.data && response.status === HTTP_STATUS_CODE.OK) {
        promoCodeStore.updatePromoCode(id, { isActive });
        toastStore.success(
          isActive ? 'Code promo activé avec succès' : 'Code promo désactivé avec succès'
        );
        return true;
      } else {
        this.handleError(response.message || 'Erreur lors du changement de statut du code promo');
        return false;
      }
    } catch (error: any) {
      this.handleError(
        error.message || 'Erreur lors du changement de statut du code promo',
        error
      );
      return false;
    }
  }

  /**
   * Supprime un code promo
   */
  async deletePromoCode(id: string): Promise<boolean> {
    try {
      const response = await this.promoCodeModel.deletePromoCode(id);

      if (response.success && response.status === HTTP_STATUS_CODE.OK) {
        promoCodeStore.removePromoCode(id);
        toastStore.success(response.message || 'Code promo supprimé avec succès');
        return true;
      } else {
        this.handleError(response.message || 'Erreur lors de la suppression du code promo');
        return false;
      }
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la suppression du code promo', error);
      return false;
    }
  }

  /**
   * Charge les statistiques d'un code promo
   */
  async loadPromoCodeStats(id: string): Promise<any> {
    try {
      const response = await this.promoCodeModel.getPromoCodeStats(id);

      if (response.success && response.data) {
        return response.data;
      } else {
        this.handleError('Erreur lors du chargement des statistiques');
        return null;
      }
    } catch (error: any) {
      this.handleError('Erreur lors du chargement des statistiques', error);
      return null;
    }
  }

  /**
   * Valide un code promo
   */
  async validatePromoCode(data: {
    code: string;
    clientId: string;
    companyId: string;
    amount: number;
  }): Promise<any> {
    try {
      const response = await this.promoCodeModel.validatePromoCode(data);

      if (response.success && response.data) {
        return response.data;
      } else {
        return null;
      }
    } catch (error: any) {
      console.error('Erreur lors de la validation du code promo', error);
      return null;
    }
  }

  /**
   * Envoie un code promo par email aux clients sélectionnés
   */
  async sendPromoCodeEmail(promoCodeId: string, clientIds: string[]): Promise<boolean> {
    this.setLoadingStates(true);

    try {
      const response = await this.promoCodeModel.sendPromoCodeEmail(promoCodeId, clientIds);

      if (response.success) {
        toastStore.success(response.message || 'Emails envoyés avec succès');
        return true;
      } else {
        this.handleError(response.message || 'Erreur lors de l\'envoi des emails');
        return false;
      }
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de l\'envoi des emails', error);
      return false;
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Réinitialise le store
   */
  reset(): void {
    promoCodeStore.reset();
  }
}

export default new PromoCodeViewModel();
